/**
 * Sheet 07b — MFA enrolment and challenge (A.4, SEC.7; FR-011, FR-020, FR-021;
 * callouts 3, 4, 5, 8).
 *
 * Mode comes from the `next` the previous screen put in router state
 * (`mfa_enrol` after signup or a first login, `mfa_challenge` after a login by
 * an enrolled identity). The tenant query cannot tell the two apart: both a
 * pre-login and a challenge session answer `403 mfa-required`. Without state
 * (a refresh) the screen tries to enrol; a `401` there means the cookie is a
 * challenge cookie, so it falls back to challenge mode. A full session (the
 * tenant query has data) is already enrolled and goes straight to the app.
 *
 * Enrol: the API returns the pending secret once; the QR is rendered in the
 * browser (OtpQr) and the manual key in mono, grouped in fours. Confirm returns
 * the eight backup codes exactly once. They are held in this component's state
 * only, never in the query cache or the URL, and cleared on Continue so a back
 * navigation cannot show them again. `next` from the confirm response routes
 * to /verify or the app without a second round trip (research §1.13).
 *
 * Challenge: a TOTP or a backup code. `invalid-credentials` shows the same
 * sentence as login — a wrong code, a replayed code, an expired challenge and
 * a lockout are one body by design (SEC.7). A `401 unauthorized` means the
 * challenge cookie is gone; the password must be entered again.
 *
 * Every transition drops the cached tenant query before navigating so the next
 * gate refetches instead of acting on the stale 403 (see AuthGate).
 *
 * Every exit into the app lands on router state's `returnTo`, the path a
 * session expiry left (Login carries it), through `safeReturnPath`: an /app
 * path of this origin, or APP_HOME (FR-026; follow-up 14; U26). The exits to
 * /login and /verify are unchanged.
 *
 * This screen reads the tenant query with `retryOnMount: false`. The gate has
 * already settled it as a 403; a second observer that retried on mount would
 * refetch it, and a GET /v1/tenant on every mount is one loop too many (the
 * route-surface test pins one fetch and one enrol call). The gate, for its
 * part, keeps this screen mounted across any refetch after that first answer
 * and never refetches on window focus or reconnect (AuthGate): TanStack takes
 * a data-less query back through `pending` while it refetches, and an unmount
 * here would call enrol again on remount, which rotates the pending secret
 * (contracts/auth-api.md) and invalidates a QR the user may already have
 * scanned, and would drop the once-shown backup codes. `Enrol` also guards
 * its own start with a ref, so a re-render with `active` flipping false and
 * back during such a refetch does not enrol twice.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import {
  PROBLEM,
  isProblem,
  logout,
  mfaChallenge,
  mfaConfirm,
  mfaEnrol,
  type MfaEnrolResponse,
} from '@/lib/auth'
import { safeReturnPath } from '@/lib/session'
import { formatManualKey } from '@/lib/totp-format'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { clearSessionCache, tenantQueryOptions } from '@/components/trellis/AuthGate'
import { BackupCodes } from '@/components/trellis/BackupCodes'
import { OtpQr } from '@/components/trellis/OtpQr'
import { AuthLayout, DisabledReason, failureCopy } from './AuthLayout'
import { LOGIN_FAILED_MESSAGE } from './Login'

type Mode = 'enrol' | 'challenge'
interface MfaState {
  next?: 'mfa_enrol' | 'mfa_challenge'
  /** The path a session expiry left; untrusted until `safeReturnPath` (FR-026). */
  returnTo?: string
}

const TOTP = /^\d{6}$/
const INVALID_CODE_MESSAGE = 'That code is not right. Codes change every 30 seconds; enter the one showing now.'

export default function Mfa() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const state = useLocation().state as MfaState | null
  const stated = state?.next
  // Not useTenant(): see the header. The gate owns fetching; this only observes.
  const tenant = useQuery({ ...tenantQueryOptions, retryOnMount: false })
  const [mode, setMode] = useState<Mode>(stated === 'mfa_challenge' ? 'challenge' : 'enrol')
  const appPath = safeReturnPath(state?.returnTo, window.location.origin)

  // Every exit drops every cached query: leaving for the app completes a
  // sign-in, so nothing another tenant cached may render next (TEN.1; mission
  // 6 review follow-up 14, cycle 2 finding 1).
  const leave = useCallback(
    (to: string) => {
      clearSessionCache(queryClient)
      navigate(to, { replace: true })
    },
    [navigate, queryClient],
  )

  // A full session is already enrolled; nothing to do here (409 on enrol otherwise).
  useEffect(() => {
    if (tenant.data) leave(appPath)
  }, [tenant.data, leave, appPath])

  return (
    <>
      {mode === 'enrol' ? (
        <Enrol
          active={tenant.status === 'error'}
          onChallenge={stated ? null : () => setMode('challenge')}
          leave={leave}
          appPath={appPath}
        />
      ) : (
        <Challenge leave={leave} appPath={appPath} />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------

interface EnrolProps {
  /** Start enrolling only once the tenant query has settled as a partial session. */
  active: boolean
  /** Set when no `next` was given: a 401 on enrol means a challenge cookie. */
  onChallenge: (() => void) | null
  leave: (to: string) => void
  /** Where a completed sign-in lands: `safeReturnPath` of router state's `returnTo`. */
  appPath: string
}

function Enrol({ active, onChallenge, leave, appPath }: EnrolProps) {
  const started = useRef(false)
  const [enrolment, setEnrolment] = useState<MfaEnrolResponse | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [codes, setCodes] = useState<string[]>([])
  const [next, setNext] = useState<'verify' | 'app'>('verify')
  const codeId = useId()
  const reasonId = useId()

  const start = useCallback(async () => {
    setStartError(null)
    try {
      setEnrolment(await mfaEnrol())
    } catch (err) {
      if (isProblem(err, PROBLEM.ALREADY_ENROLLED)) {
        // Only a stale pre_mfa cookie on an identity that enrolled elsewhere
        // reaches here (a full session never mounts Enrol). Going to the app
        // would loop through the gate's 403 and back; end the session instead
        // so the next login issues a challenge.
        await logout().catch(() => undefined)
        leave('/login')
      } else if (isProblem(err, PROBLEM.UNAUTHORIZED) && onChallenge) onChallenge()
      else if (isProblem(err, PROBLEM.UNAUTHORIZED)) leave('/login')
      else setStartError(failureCopy(err))
    }
  }, [leave, onChallenge])

  useEffect(() => {
    if (!active || started.current) return
    started.current = true
    void start()
  }, [active, start])

  const reason = TOTP.test(code) ? null : 'Confirm is unavailable until you enter the 6-digit code.'
  const canSubmit = reason === null && !submitting

  async function onConfirm(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setCodeError(null)
    try {
      const res = await mfaConfirm({ code })
      // The secret has served its purpose; drop it with the QR.
      setEnrolment(null)
      setCode('')
      setCodes(res.backup_codes)
      setNext(res.next)
    } catch (err) {
      if (isProblem(err, PROBLEM.INVALID_CODE)) setCodeError(INVALID_CODE_MESSAGE)
      else if (isProblem(err, PROBLEM.UNAUTHORIZED)) leave('/login')
      else setCodeError(failureCopy(err))
    } finally {
      setSubmitting(false)
    }
  }

  function onContinue() {
    setCodes([])
    leave(next === 'verify' ? '/verify' : appPath)
  }

  if (codes.length > 0) {
    return (
      <AuthLayout title="Save your backup codes" lede="Your authenticator is set up. These codes are shown once.">
        <BackupCodes codes={codes} onContinue={onContinue} />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Set up your authenticator"
      lede="Every Trellis login needs a code from an authenticator app. Scan the QR code or enter the manual key, then confirm with the code the app shows."
    >
      {startError && (
        <Alert variant="blocked">
          <AlertTitle>Trellis could not start enrolment</AlertTitle>
          <AlertDescription>
            <p>{startError}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void start()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {enrolment && (
        <form className="flex flex-col gap-5" onSubmit={(e) => void onConfirm(e)} noValidate>
          <OtpQr uri={enrolment.otpauth_uri} />
          <div className="flex flex-col gap-1.5">
            <p className="text-body-sm font-medium text-navy-950">Manual key</p>
            <p className="rounded-md border border-navy-200 bg-navy-50 px-3 py-2 font-mono text-body tracking-wide text-navy-950">
              {formatManualKey(enrolment.secret)}
            </p>
            <p className="text-caption text-navy-600">
              If you cannot scan the code, add an account in your app by typing this key. Account name:
              Trellis.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={codeId}>Code from your authenticator app</Label>
            <Input
              id={codeId}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              className="max-w-[160px] font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              aria-invalid={codeError ? true : undefined}
            />
          </div>

          {codeError && (
            <Alert variant="blocked">
              <AlertDescription>
                <p>{codeError}</p>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit}
              aria-busy={submitting || undefined}
              aria-describedby={reason ? reasonId : undefined}
            >
              Confirm
            </Button>
            {reason && <DisabledReason id={reasonId}>{reason}</DisabledReason>}
          </div>
        </form>
      )}
    </AuthLayout>
  )
}

// ---------------------------------------------------------------------------

function Challenge({ leave, appPath }: { leave: (to: string) => void; appPath: string }) {
  const [backup, setBackup] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const codeId = useId()
  const reasonId = useId()

  const complete = backup ? code.trim().length > 0 : TOTP.test(code)
  const reason = complete
    ? null
    : backup
      ? 'Log in is unavailable until you enter a backup code.'
      : 'Log in is unavailable until you enter the 6-digit code.'
  const canSubmit = reason === null && !submitting

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await mfaChallenge({ code: code.trim() })
      leave(appPath)
    } catch (err) {
      if (isProblem(err, PROBLEM.INVALID_CREDENTIALS)) setError(LOGIN_FAILED_MESSAGE)
      else if (isProblem(err, PROBLEM.UNAUTHORIZED)) leave('/login')
      else setError(failureCopy(err))
    } finally {
      setSubmitting(false)
    }
  }

  function toggle() {
    setBackup((b) => !b)
    setCode('')
    setError(null)
  }

  return (
    <AuthLayout
      title="Enter your authenticator code"
      lede={
        backup
          ? 'Enter one of the backup codes you saved when you set up your authenticator. Each code works once.'
          : 'Open your authenticator app and enter the 6-digit code it shows for Trellis.'
      }
      idleNotice
    >
      <form className="flex flex-col gap-5" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={codeId}>{backup ? 'Backup code' : 'Code from your authenticator app'}</Label>
          <Input
            id={codeId}
            inputMode={backup ? 'text' : 'numeric'}
            autoComplete={backup ? 'off' : 'one-time-code'}
            maxLength={backup ? 11 : 6}
            placeholder={backup ? 'xxxxx-xxxxx' : '123456'}
            className="max-w-[200px] font-mono"
            value={code}
            onChange={(e) => setCode(backup ? e.target.value : e.target.value.replace(/\D/g, ''))}
            aria-invalid={error ? true : undefined}
          />
        </div>

        {error && (
          <Alert variant="blocked">
            <AlertDescription>
              <p>
                {error}{' '}
                <Link to="/login" className="text-[var(--color-link)] underline">
                  Log in again
                </Link>{' '}
                if this keeps happening.
              </p>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            size="lg"
            disabled={!canSubmit}
            aria-busy={submitting || undefined}
            aria-describedby={reason ? reasonId : undefined}
          >
            Log in
          </Button>
          {reason && <DisabledReason id={reasonId}>{reason}</DisabledReason>}
        </div>

        <Button variant="link" className="self-start" onClick={toggle}>
          {backup ? 'Use your authenticator app instead' : 'Use a backup code'}
        </Button>
      </form>
    </AuthLayout>
  )
}
